// TransferCustomerModal.js

import React, { useState, useCallback, useEffect } from 'react';
import {
  Modal,
  Typography,
  Box,
  Button,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Backdrop,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  getFirestore,
  collection,
  query,
  where,
  getDocs,
  writeBatch,
  doc,
  deleteDoc,
  getDoc,
  setDoc,
  updateDoc,
  runTransaction,
  collectionGroup,
  serverTimestamp,
} from 'firebase/firestore';
import CustomerSelectionTable from './CustomerSelectionTable';
import { auth } from './firebaseConfig';

// ==================== KİLİTLEME MEKANİZMASI ====================

/**
 * Transfer kilidi oluşturur - işlem başlamadan önce çağrılır
 * Eğer kilit zaten varsa (başka işlem devam ediyorsa) hata fırlatır
 */
const acquireTransferLock = async (userEmail, sourceCustomerId, targetCustomerId) => {
  const db = getFirestore();
  const lockRef = doc(db, `users/${userEmail}/transferLocks/active`);
  
  return await runTransaction(db, async (transaction) => {
    const lockDoc = await transaction.get(lockRef);
    
    if (lockDoc.exists()) {
      const lockData = lockDoc.data();
      const lockTime = lockData.lockedAt?.toDate?.() || new Date(lockData.lockedAt);
      const now = new Date();
      const lockAge = (now - lockTime) / 1000; // saniye
      
      // Kilit 5 dakikadan eskiyse, stale kabul et ve üzerine yaz
      if (lockAge < 300) {
        throw new Error(
          `Başka bir transfer işlemi devam ediyor. ` +
          `(${lockData.sourceCustomerName} → ${lockData.targetCustomerName}) ` +
          `Lütfen ${Math.ceil(300 - lockAge)} saniye bekleyin veya sayfayı yenileyin.`
        );
      }
    }
    
    // Yeni kilit oluştur
    const lockData = {
      sourceCustomerId,
      targetCustomerId,
      sourceCustomerName: '', // Sonra güncellenecek
      targetCustomerName: '', // Sonra güncellenecek
      lockedAt: serverTimestamp(),
      lockedBy: userEmail,
      status: 'processing'
    };
    
    transaction.set(lockRef, lockData);
    return true;
  });
};

/**
 * Transfer kilidini günceller (müşteri isimleri ile)
 */
const updateTransferLock = async (userEmail, sourceCustomerName, targetCustomerName) => {
  const db = getFirestore();
  const lockRef = doc(db, `users/${userEmail}/transferLocks/active`);
  
  try {
    await updateDoc(lockRef, {
      sourceCustomerName,
      targetCustomerName
    });
  } catch (error) {
    console.warn('Kilit güncellenirken hata (kritik değil):', error);
  }
};

/**
 * Transfer kilidini serbest bırakır - işlem tamamlandığında veya hata durumunda çağrılır
 */
const releaseTransferLock = async (userEmail) => {
  const db = getFirestore();
  const lockRef = doc(db, `users/${userEmail}/transferLocks/active`);
  
  try {
    await deleteDoc(lockRef);
    console.log('🔓 Transfer kilidi serbest bırakıldı');
  } catch (error) {
    console.error('Kilit serbest bırakılırken hata:', error);
  }
};

/**
 * Aktif transfer kilidi var mı kontrol eder
 */
const checkTransferLock = async (userEmail) => {
  const db = getFirestore();
  const lockRef = doc(db, `users/${userEmail}/transferLocks/active`);
  
  try {
    const lockDoc = await getDoc(lockRef);
    
    if (lockDoc.exists()) {
      const lockData = lockDoc.data();
      const lockTime = lockData.lockedAt?.toDate?.() || new Date(lockData.lockedAt);
      const now = new Date();
      const lockAge = (now - lockTime) / 1000;
      
      // 5 dakikadan eski kilitleri geçersiz say
      if (lockAge < 300) {
        return {
          isLocked: true,
          ...lockData,
          remainingSeconds: Math.ceil(300 - lockAge)
        };
      }
    }
    
    return { isLocked: false };
  } catch (error) {
    console.error('Kilit kontrolü sırasında hata:', error);
    return { isLocked: false };
  }
};

// ==================== KİLİTLEME MEKANİZMASI SONU ====================

// ==================== YARDIMCI FONKSİYONLAR ====================

/**
 * Firebase/Firestore hatalarını Türkçe ve kullanıcı dostu mesajlara çevirir
 */
const classifyFirebaseError = (error) => {
  const errorCode = error.code || '';
  const errorMessage = error.message || '';
  
  // Firebase Firestore hata kodları
  const errorMap = {
    'permission-denied': 'Yetki hatası! Bu işlemi gerçekleştirme yetkiniz yok. Lütfen yöneticinize başvurun.',
    'not-found': 'Kayıt bulunamadı. Müşteri başka biri tarafından silinmiş olabilir. Sayfayı yenileyip tekrar deneyin.',
    'unavailable': 'Sunucuya bağlanılamıyor. İnternet bağlantınızı kontrol edip tekrar deneyin.',
    'deadline-exceeded': 'İşlem zaman aşımına uğradı. Lütfen tekrar deneyin.',
    'resource-exhausted': 'Firebase limiti aşıldı. Lütfen birkaç dakika bekleyip tekrar deneyin.',
    'cancelled': 'İşlem iptal edildi. Lütfen tekrar deneyin.',
    'already-exists': 'Bu kayıt zaten mevcut.',
    'failed-precondition': 'İşlem gerçekleştirilemedi. Veriler değişmiş olabilir, sayfayı yenileyip tekrar deneyin.',
    'aborted': 'İşlem çakışması oluştu. Lütfen tekrar deneyin.',
    'internal': 'Sunucu hatası oluştu. Lütfen daha sonra tekrar deneyin.',
    'data-loss': 'Veri kaybı riski! Lütfen destek ile iletişime geçin.',
    'unauthenticated': 'Oturum süresi dolmuş. Lütfen sayfayı yenileyip tekrar giriş yapın.',
    'invalid-argument': 'Geçersiz veri formatı. Lütfen müşteri bilgilerini kontrol edin.',
  };
  
  // Hata koduna göre mesaj döndür
  if (errorCode && errorMap[errorCode]) {
    return errorMap[errorCode];
  }
  
  // Network hataları
  if (errorMessage.toLowerCase().includes('network') || 
      errorMessage.toLowerCase().includes('internet') ||
      errorMessage.toLowerCase().includes('connection')) {
    return 'İnternet bağlantı hatası. Bağlantınızı kontrol edip tekrar deneyin.';
  }
  
  // Timeout hataları
  if (errorMessage.toLowerCase().includes('timeout') || 
      errorMessage.toLowerCase().includes('timed out')) {
    return 'İşlem zaman aşımına uğradı. Lütfen tekrar deneyin.';
  }
  
  // Bilinmeyen hatalar için orijinal mesajı döndür
  return `Veri transferi sırasında bir hata oluştu: ${errorMessage || 'Bilinmeyen hata'}. Lütfen tekrar deneyin.`;
};

/**
 * Müşteri adını normalize eder (Türkçe karakter, boşluk, büyük/küçük harf)
 */
const normalizeCustomerName = (name) => {
  if (!name) return '';
  
  let normalized = name;
  
  // 1. Baş ve sondaki boşlukları temizle
  normalized = normalized.trim();
  
  // 2. Çoklu boşlukları tek boşluğa çevir
  normalized = normalized.replace(/\s+/g, ' ');
  
  // 3. Türkçe karakterleri normalize et ve küçük harfe çevir
  normalized = normalized.toLocaleLowerCase('tr-TR');
  
  return normalized;
};

/**
 * İki müşteri adının normalize edilmiş hallerini karşılaştırır
 */
const customerNamesMatch = (name1, name2) => {
  return normalizeCustomerName(name1) === normalizeCustomerName(name2);
};

/**
 * Benzer müşteri adlarını bulur (ilk 5 karakter eşleşmesi)
 */
const findSimilarNames = (targetName, allNames) => {
  const normalizedTarget = normalizeCustomerName(targetName);
  if (normalizedTarget.length < 5) return [];
  
  const targetPrefix = normalizedTarget.substring(0, 5);
  const similar = new Set();
  
  for (const name of allNames) {
    if (!name) continue;
    const normalizedName = normalizeCustomerName(name);
    
    // Tam normalize eşleşme (ama orijinal farklı)
    if (normalizedName === normalizedTarget && name !== targetName) {
      similar.add(name);
    }
    // İlk 5 karakter eşleşmesi
    else if (normalizedName.length >= 5 && normalizedName.substring(0, 5) === targetPrefix) {
      similar.add(name);
    }
  }
  
  return Array.from(similar);
};

// ==================== VALİDASYON FONKSİYONLARI ====================

/**
 * Transfer öncesi validasyonları yapar
 * @returns {Object} { valid: boolean, error: string|null, warning: string|null, stats: { timeSheetsCount, debtDocsCount } }
 */
const validateTransfer = async (userEmail, sourceCustomer, targetCustomer, isSantiyeTransfer) => {
  const db = getFirestore();
  const sourceName = isSantiyeTransfer 
    ? sourceCustomer['Şantiye Adı'] || sourceCustomer['Müşteri Adı']
    : sourceCustomer['Müşteri Adı'];

  console.log('🔍 [VALIDATION] Transfer validasyonu başlıyor...');

  // 1. Aynı müşteri kontrolü
  if (sourceCustomer.id === targetCustomer.id) {
    return { valid: false, error: 'Kaynak ve hedef müşteri aynı olamaz', stats: null };
  }

  // 2. ID kontrolü
  if (!sourceCustomer.id) {
    return { valid: false, error: 'Kaynak müşteri ID\'si bulunamadı (muhtemelen zaten silinmiş)', stats: null };
  }

  if (!targetCustomer.id) {
    return { valid: false, error: 'Hedef müşteri ID\'si bulunamadı', stats: null };
  }

  // 3. Kaynak müşteri hala mevcut mu?
  try {
    const sourceRef = doc(db, `users/${userEmail}/customerList/${sourceCustomer.id}`);
    const sourceDoc = await getDoc(sourceRef);
    if (!sourceDoc.exists()) {
      return { valid: false, error: 'Kaynak müşteri veritabanında bulunamadı (muhtemelen başka biri silmiş)', stats: null };
    }
  } catch (err) {
    return { valid: false, error: `Kaynak müşteri kontrolü hatası: ${err.message}`, stats: null };
  }

  // 4. Hedef müşteri hala mevcut mu?
  try {
    const targetRef = doc(db, `users/${userEmail}/customerList/${targetCustomer.id}`);
    const targetDoc = await getDoc(targetRef);
    if (!targetDoc.exists()) {
      return { valid: false, error: 'Hedef müşteri veritabanında bulunamadı', stats: null };
    }
    
    // Onay kontrolü
    const targetData = targetDoc.data();
    if (targetData.Onay && targetData.Onay !== 'Onaylandı') {
      console.warn('⚠️ [VALIDATION] Hedef müşteri onaylı değil:', targetData.Onay);
      // Uyarı ama engelleme
    }
  } catch (err) {
    return { valid: false, error: `Hedef müşteri kontrolü hatası: ${err.message}`, stats: null };
  }

  // 5. Puantaj ve batch limit kontrolü
  try {
    // TimeSheets sayısı
    const timeSheetsRef = collection(db, `users/${userEmail}/timeSheets`);
    const timeSheetsQuery = query(timeSheetsRef, where('Müşteri Adı', '==', sourceName));
    const timeSheetsSnapshot = await getDocs(timeSheetsQuery);
    let timeSheetsCount = timeSheetsSnapshot.size;
    
    let warning = null;
    let similarNames = [];

    // 6. Eğer puantaj bulunamadıysa, benzer isimleri kontrol et
    if (timeSheetsCount === 0) {
      console.log('⚠️ [VALIDATION] Puantaj bulunamadı, benzer isimler aranıyor...');
      
      // Tüm puantajları çek ve benzer isimleri bul
      const allTimeSheetsRef = collection(db, `users/${userEmail}/timeSheets`);
      const allTimeSheetsSnapshot = await getDocs(allTimeSheetsRef);
      
      const allCustomerNames = allTimeSheetsSnapshot.docs.map(doc => doc.data()['Müşteri Adı']).filter(Boolean);
      similarNames = findSimilarNames(sourceName, allCustomerNames);
      
      // Normalize eşleşen puantajları say
      let normalizeMatchCount = 0;
      for (const doc of allTimeSheetsSnapshot.docs) {
        const docCustomerName = doc.data()['Müşteri Adı'];
        if (docCustomerName && customerNamesMatch(docCustomerName, sourceName)) {
          normalizeMatchCount++;
        }
      }
      
      if (normalizeMatchCount > 0) {
        timeSheetsCount = normalizeMatchCount;
        console.log(`📊 [VALIDATION] Normalize eşleşme ile ${normalizeMatchCount} puantaj bulundu`);
      } else if (similarNames.length > 0) {
        console.log('📋 [VALIDATION] Benzer müşteri adları bulundu:', similarNames.slice(0, 5));
        warning = `'${sourceName}' için puantaj bulunamadı. Benzer isimler: ${similarNames.slice(0, 3).join(', ')}. Müşteri adını kontrol edin.`;
      } else {
        console.log('ℹ️ [VALIDATION] Benzer isim de bulunamadı. Sadece cari aktarılacak.');
        warning = `'${sourceName}' için puantaj bulunamadı. Sadece cari bilgileri aktarılacak.`;
      }
    }

    // 7. Debt docs sayısı
    const debtRef = collection(db, `users/${userEmail}/customerList/${sourceCustomer.id}/debt`);
    const debtSnapshot = await getDocs(debtRef);
    const debtDocsCount = debtSnapshot.size;

    // 8. Debt history boyut kontrolü
    const sourceDebtCurrentRef = doc(db, `users/${userEmail}/customerList/${sourceCustomer.id}/debt/current`);
    const sourceDebtDoc = await getDoc(sourceDebtCurrentRef);
    
    if (sourceDebtDoc.exists()) {
      const sourceData = sourceDebtDoc.data();
      const sourceHistory = sourceData.history || [];
      
      // Hedef müşterinin history'sini de kontrol et
      const targetDebtCurrentRef = doc(db, `users/${userEmail}/customerList/${targetCustomer.id}/debt/current`);
      const targetDebtDoc = await getDoc(targetDebtCurrentRef);
      
      let targetHistoryCount = 0;
      if (targetDebtDoc.exists()) {
        const targetData = targetDebtDoc.data();
        targetHistoryCount = (targetData.history || []).length;
      }
      
      // Her history item yaklaşık 500 byte
      const totalEstimatedSizeKB = Math.round(((sourceHistory.length + targetHistoryCount) * 500) / 1024);
      
      if (totalEstimatedSizeKB > 900) { // 900KB güvenli marj (Firestore 1MB limit)
        return { 
          valid: false, 
          error: `Cari geçmişi çok büyük (~${totalEstimatedSizeKB} KB). Maksimum 900 KB destekleniyor. Lütfen eski kayıtları temizleyin.`,
          stats: null 
        };
      }
      
      console.log(`📊 [VALIDATION] Debt history boyutu: ~${totalEstimatedSizeKB} KB (kaynak: ${sourceHistory.length}, hedef: ${targetHistoryCount} kayıt)`);
    }

    // 9. Şantiye sayısı (müşteri transferinde)
    let shantiyeCount = 0;
    if (!isSantiyeTransfer) {
      const customerListRef = collection(db, `users/${userEmail}/customerList`);
      const shantiyeQuery = query(customerListRef, where('parentId', '==', sourceCustomer.id));
      const shantiyeSnapshot = await getDocs(shantiyeQuery);
      shantiyeCount = shantiyeSnapshot.size;
    }

    // 10. Toplam işlem sayısı hesapla
    // Her timeSheet = 1, her debt doc = 1, her şantiye = 2 (update + debt delete), customer delete = 1, target debt update = 1
    const totalOperations = timeSheetsCount + debtDocsCount + (shantiyeCount * 2) + 2;

    console.log('📊 [VALIDATION] İşlem sayıları:', {
      timeSheetsCount,
      debtDocsCount,
      shantiyeCount,
      totalOperations
    });

    if (totalOperations > 450) {
      return { 
        valid: false, 
        error: `Transfer edilecek veri çok fazla (${totalOperations} işlem). Maksimum 450 işlem destekleniyor. Lütfen önce bazı verileri manuel olarak taşıyın.`,
        stats: { timeSheetsCount, debtDocsCount, shantiyeCount, totalOperations }
      };
    }

    return { 
      valid: true, 
      error: null,
      warning, // Uyarı mesajı (puantaj bulunamadı vs.)
      similarNames, // Benzer isimler
      stats: { timeSheetsCount, debtDocsCount, shantiyeCount, totalOperations } 
    };

  } catch (err) {
    return { valid: false, error: `Batch limit kontrolü hatası: ${err.message}`, stats: null };
  }
};

/**
 * Transfer işlemini activity log'a kaydeder
 */
const logTransferActivity = async (userEmail, sourceCustomer, targetCustomer, stats, success, errorMessage = null) => {
  const db = getFirestore();
  
  try {
    const activityRef = doc(collection(db, `users/${userEmail}/activityLogs`));
    
    const logData = {
      type: 'customer_transfer',
      timestamp: serverTimestamp(),
      userEmail,
      sourceCustomerId: sourceCustomer.id || '',
      sourceCustomerName: sourceCustomer['Müşteri Adı'] || sourceCustomer['Şantiye Adı'] || '',
      sourceCariCode: sourceCustomer.cariCode || '',
      targetCustomerId: targetCustomer.id || '',
      targetCustomerName: targetCustomer['Müşteri Adı'] || '',
      targetCariCode: targetCustomer.cariCode || '',
      timeSheetsTransferred: stats?.timeSheetsCount || 0,
      debtDocsTransferred: stats?.debtDocsCount || 0,
      shantiyeCount: stats?.shantiyeCount || 0,
      success,
      platform: 'web',
      userAgent: navigator.userAgent
    };

    if (errorMessage) {
      logData.errorMessage = errorMessage;
    }

    await setDoc(activityRef, logData);
    console.log('📝 [AUDIT] Transfer activity logged');

  } catch (err) {
    console.error('⚠️ [AUDIT] Activity log yazılamadı:', err);
    // Activity log hatası transfer'i engellemez
  }
};

// ==================== VALİDASYON FONKSİYONLARI SONU ====================

// Debt subcollection'ını silen fonksiyon
const deleteDebtSubcollection = async (userEmail, customerId) => {
  const db = getFirestore();
  
  try {
    // Debt subcollection'ındaki tüm dökümanları al
    const debtCollectionRef = collection(db, `users/${userEmail}/customerList/${customerId}/debt`);
    const debtSnapshot = await getDocs(debtCollectionRef);
    
    // Batch ile tüm debt dökümanlarını sil
    const batch = writeBatch(db);
    debtSnapshot.docs.forEach((debtDoc) => {
      batch.delete(debtDoc.ref);
    });
    
    if (debtSnapshot.docs.length > 0) {
      await batch.commit();
      console.log(`🗑️ ${customerId} müşterisinin ${debtSnapshot.docs.length} adet debt kaydı silindi`);
    }
  } catch (error) {
    console.error('Debt subcollection silinirken hata:', error);
    throw error;
  }
};

// Müşteriyi debt subcollection'ı ile birlikte silen fonksiyon
const deleteCustomerWithDebt = async (userEmail, customerId, existingBatch = null) => {
  const db = getFirestore();
  
  try {
    // Önce debt subcollection'ını sil
    await deleteDebtSubcollection(userEmail, customerId);
    
    // Sonra müşteriyi sil
    const customerRef = doc(db, `users/${userEmail}/customerList/${customerId}`);
    
    if (existingBatch) {
      // Mevcut batch'e ekle
      existingBatch.delete(customerRef);
    } else {
      // Direkt sil
      await deleteDoc(customerRef);
    }
    
    console.log(`🗑️ ${customerId} müşterisi debt subcollection'ı ile birlikte silindi`);
  } catch (error) {
    console.error('Müşteri ve debt subcollection silinirken hata:', error);
    throw error;
  }
};

// Bakiye aktarım fonksiyonu
const transferCustomerDebt = async (userEmail, sourceCustomerId, targetCustomerId, sourceName, targetName) => {
  const db = getFirestore();
  
  try {
    // Kaynak müşterinin borç bilgisini al
    const sourceDebtRef = doc(db, `users/${userEmail}/customerList/${sourceCustomerId}/debt/current`);
    const sourceDebtDoc = await getDoc(sourceDebtRef);
    
    if (!sourceDebtDoc.exists()) {
      console.log('Kaynak müşterinin borç bilgisi bulunamadı');
      return;
    }
    
    const sourceDebtData = sourceDebtDoc.data();
    const sourceAmount = sourceDebtData.amount || 0;
    const sourceHistory = sourceDebtData.history || [];
    
    if (sourceAmount === 0 && sourceHistory.length === 0) {
      console.log('Aktarılacak borç veya geçmiş bulunamadı');
      return;
    }
    
    // Hedef müşterinin borç referansı
    const targetDebtRef = doc(db, `users/${userEmail}/customerList/${targetCustomerId}/debt/current`);
    
    // Transaction kullanarak atomik işlem yap
    await runTransaction(db, async (transaction) => {
      const targetDebtDoc = await transaction.get(targetDebtRef);
      
      // Hedef müşterinin mevcut borç miktarını al
      const targetCurrentAmount = targetDebtDoc.exists() ? (targetDebtDoc.data().amount || 0) : 0;
      const targetCurrentHistory = targetDebtDoc.exists() ? (targetDebtDoc.data().history || []) : [];
      
      // Yeni borç miktarını hesapla - sadece aktarılan puantajların toplamı
      // sourceAmount zaten kaynak müşterinin toplam borcuydu, bunu direkt ekle
      const newTargetAmount = targetCurrentAmount + sourceAmount;
      
      // Tarih formatı
      const currentTimeString = new Date().toISOString();
      
      // Transfer bilgi kaydı oluştur (miktar 0 - sadece bilgi amaçlı)
      const transferInfoEntry = {
        amount: 0, // Sıfır - ekstra borç yaratmaz
        date: currentTimeString,
        description: `${sourceName} müşterisinden aktarıldı`,
        type: 'transfer_in',
        transferFrom: sourceName,
        transferTo: targetName,
        isInfoOnly: true // Sadece bilgi amaçlı işaret
      };
      
      // Kaynak müşterinin tüm history'sini hedef müşteriye ekle (transfer olarak işaretle)
      const transferredHistory = sourceHistory.map(historyItem => ({
        ...historyItem,
        transferredFrom: sourceName,
        transferredAt: currentTimeString
      }));
      
      // Hedef müşterinin history'sini güncelle
      const updatedTargetHistory = [
        ...targetCurrentHistory,
        transferInfoEntry, // Bilgi kaydı
        ...transferredHistory // Gerçek puantaj kayıtları
      ];
      
      // Hedef müşterinin borç bilgilerini güncelle
      const targetUpdateData = {
        amount: newTargetAmount,
        history: updatedTargetHistory,
        lastUpdated: currentTimeString,
        customerName: targetName
      };
      
      // Hedef müşteriye kaydet
      transaction.set(targetDebtRef, targetUpdateData);
      
      // Kaynak müşterinin borç bilgisini sıfırla
      const sourceUpdateData = {
        amount: 0,
        history: [],
        lastUpdated: currentTimeString,
        customerName: sourceName,
        transferredTo: targetName,
        transferredAt: currentTimeString
      };
      
      // Kaynak müşteriye kaydet
      transaction.set(sourceDebtRef, sourceUpdateData);
      
      console.log('🏦 Bakiye aktarımı tamamlandı:', {
        kaynak: sourceName,
        hedef: targetName,
        aktarılanMiktar: sourceAmount,
        hedefYeniBakiye: newTargetAmount,
        aktarılanGeçmişSayısı: sourceHistory.length
      });
    });
    
  } catch (error) {
    console.error('Bakiye aktarımı sırasında hata:', error);
    throw error;
  }
};

const ModalBox = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 600,
  backgroundColor: '#ffffff',
  color: '#000000',
  boxShadow: theme.shadows[5],
  padding: theme.spacing(4),
  maxHeight: '90vh',
  overflowY: 'auto',
  border: '1px solid #ccc',
}));

export const handleDataTransfer = async ({
  sourceCustomer,
  targetCustomer,
  isSantiyeTransfer,
  setError,
  setSuccessMessage,
  setAlertOpen,
  onSuccess,
  setIsProcessing // Yeni: UI'da loading göstermek için
}) => {
  if (!targetCustomer) {
    setError(`Lütfen veri aktarımı için bir ${isSantiyeTransfer ? 'şantiye' : 'müşteri'} seçin.`);
    setAlertOpen(true);
    return;
  }

  const db = getFirestore();
  const userEmail = auth.currentUser?.email;

  if (!userEmail) {
    setError('Kullanıcı oturumu bulunamadı');
    setAlertOpen(true);
    return;
  }

  const sourceName = isSantiyeTransfer 
    ? sourceCustomer['Şantiye Adı'] || sourceCustomer['Müşteri Adı']
    : sourceCustomer['Müşteri Adı'];

  // UI'da loading başlat
  if (setIsProcessing) setIsProcessing(true);

  // Validasyon sonuçlarını sakla
  let validationStats = null;

  try {
    // ==================== 1. VALİDASYON ====================
    console.log('🔍 Transfer validasyonu başlıyor...');
    
    const validation = await validateTransfer(userEmail, sourceCustomer, targetCustomer, isSantiyeTransfer);
    
    if (!validation.valid) {
      // Validasyon hatası - audit log yaz
      await logTransferActivity(userEmail, sourceCustomer, targetCustomer, null, false, `Validation failed: ${validation.error}`);
      
      setError(validation.error);
      setAlertOpen(true);
      if (setIsProcessing) setIsProcessing(false);
      return;
    }
    
    // Uyarı varsa konsola yaz (transfer devam edecek)
    if (validation.warning) {
      console.warn('⚠️ [TRANSFER] Uyarı:', validation.warning);
      // İsteğe bağlı: Kullanıcıya uyarı göster ama transferi engelleme
    }
    
    validationStats = validation.stats;
    console.log('✅ Validasyon başarılı:', validationStats);

    // ==================== 2. KİLİT AL ====================
    console.log('🔒 Transfer kilidi alınıyor...');
    
    try {
      await acquireTransferLock(userEmail, sourceCustomer.id, targetCustomer.id);
    } catch (lockError) {
      // Kilit hatası - audit log yaz
      await logTransferActivity(userEmail, sourceCustomer, targetCustomer, validationStats, false, `Lock failed: ${lockError.message}`);
      
      setError(lockError.message);
      setAlertOpen(true);
      if (setIsProcessing) setIsProcessing(false);
      return;
    }
    
    // Kilit bilgilerini güncelle
    await updateTransferLock(userEmail, sourceName, targetCustomer['Müşteri Adı']);
    console.log('🔒 Transfer kilidi alındı:', sourceName, '→', targetCustomer['Müşteri Adı']);

    // ==================== 2. VERİ AKTARIMI ====================
    const batch = writeBatch(db);
    const timeSheetsRef = collection(db, `users/${userEmail}/timeSheets`);
    const customerListRef = collection(db, `users/${userEmail}/customerList`);

    // 2.1 Puantaj verilerini aktar
    const puantajQuery = query(
      timeSheetsRef,
      where('Müşteri Adı', '==', sourceName)
    );

    const puantajSnapshot = await getDocs(puantajQuery);
    const transferCount = puantajSnapshot.size;

    puantajSnapshot.forEach((puantajDoc) => {
      const puantajRef = doc(timeSheetsRef, puantajDoc.id);
      const puantajData = puantajDoc.data();
      
      batch.update(puantajRef, {
        'Müşteri Adı': targetCustomer['Müşteri Adı'],
        'Cari Kodu': targetCustomer.cariCode,
        // Transfer izleme bilgileri
        'originalCariCode': puantajData.cariCode || puantajData['Cari Kodu'] || '',
        'transferredFrom': sourceName,
        'transferredAt': new Date().toISOString(),
        'isTransferred': true,
        'transferredBy': userEmail,
        'transferReason': `Customer data transfer: ${sourceName} → ${targetCustomer['Müşteri Adı']}`,
        'transferType': 'customer_transfer',
        'transferredTo': targetCustomer['Müşteri Adı'],
        'originalData': {
          cariCode: puantajData.cariCode || puantajData['Cari Kodu'] || '',
          customerName: sourceName,
          transferDate: new Date().toISOString()
        },
        'originalCustomerName': sourceName
      });
    });

    // 2.2 Bakiye aktarımı - Kaynak müşterinin borcunu hedef müşteriye aktar
    // ÖNEMLİ: Bu işlem transaction içinde yapılıyor, atomik
    await transferCustomerDebt(
      userEmail,
      sourceCustomer.id,
      targetCustomer.id,
      sourceName,
      targetCustomer['Müşteri Adı']
    );

    // 2.3 Müşteri/şantiye silme ve debt subcollection temizleme
    if (isSantiyeTransfer && sourceCustomer.parentId) {
      await deleteCustomerWithDebt(userEmail, sourceCustomer.id, batch);
    } else if (!isSantiyeTransfer) {
      const shantiyeQuery = query(
        customerListRef,
        where('parentId', '==', sourceCustomer.id)
      );
      
      const shantiyeSnapshot = await getDocs(shantiyeQuery);
      
      shantiyeSnapshot.forEach((shantiyeDoc) => {
        const shantiyeRef = doc(customerListRef, shantiyeDoc.id);
        const shantiyeData = shantiyeDoc.data();
        
        const oldCariCode = shantiyeData['Şantiye Cari Kodu'] || shantiyeData.cariCode;
        const shantiyeNumber = oldCariCode?.split('/')?.pop() || '';
        const newCariCode = shantiyeNumber ? `${targetCustomer.cariCode}/${shantiyeNumber}` : targetCustomer.cariCode;
        
        batch.update(shantiyeRef, {
          parentId: targetCustomer.id,
          'Şantiye Cari Kodu': newCariCode,
          cariCode: newCariCode
        });
      });
      
      for (const shantiyeDoc of shantiyeSnapshot.docs) {
        await deleteDebtSubcollection(userEmail, shantiyeDoc.id);
      }
      
      await deleteCustomerWithDebt(userEmail, sourceCustomer.id, batch);
    }

    // ==================== 3. DEĞİŞİKLİKLERİ UYGULA ====================
    await batch.commit();
    console.log('✅ Batch commit başarılı');

    // ==================== 4. KİLİDİ SERBEST BIRAK ====================
    await releaseTransferLock(userEmail);

    // ==================== 5. BAŞARILI AUDIT LOG ====================
    await logTransferActivity(userEmail, sourceCustomer, targetCustomer, {
      ...validationStats,
      timeSheetsCount: transferCount
    }, true);

    const deletedMessage = isSantiyeTransfer 
      ? 'Şantiye silindi ve verileri aktarıldı.'
      : 'Müşteri ve şantiyeleri silindi, verileri aktarıldı.';

    setSuccessMessage(`${transferCount} adet puantaj ve bakiye bilgileri başarıyla aktarıldı. ${deletedMessage}`);
    setAlertOpen(true);
    if (onSuccess) onSuccess();
    
    console.log('✅ Transfer tamamlandı:', {
      kaynak: sourceName,
      hedef: targetCustomer['Müşteri Adı'],
      taşınanVeriSayısı: transferCount,
      silinenMüşteri: sourceCustomer.id,
      işlemTipi: isSantiyeTransfer ? 'Şantiye Transfer' : 'Müşteri Transfer'
    });

  } catch (error) {
    console.error('❌ Veri transferi sırasında hata:', error);
    
    // Hata durumunda kilidi serbest bırak
    try {
      await releaseTransferLock(userEmail);
    } catch (releaseError) {
      console.error('Kilit serbest bırakılırken hata:', releaseError);
    }
    
    // Hata mesajını Türkçeleştir ve çözüm önerisi ekle
    const userFriendlyError = classifyFirebaseError(error);
    
    // ==================== 6. HATA AUDIT LOG ====================
    await logTransferActivity(userEmail, sourceCustomer, targetCustomer, validationStats, false, userFriendlyError);
    
    setError(userFriendlyError);
    setAlertOpen(true);
  } finally {
    // UI loading'i kapat
    if (setIsProcessing) setIsProcessing(false);
  }
};

const TransferCustomerModal = ({
  isOpen,
  onClose,
  selectedCustomer,
  setAlertOpen,
  setError,
  setSuccessMessage,
  isSantiyeTransfer = false,
  onSuccess,
  editName,
  editCariCode
}) => {
  const [selectedTargetCustomer, setSelectedTargetCustomer] = useState(null);
  const [availableCustomers, setAvailableCustomers] = useState([]);
  const [isConfirmTransferModalOpen, setIsConfirmTransferModalOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [activeLock, setActiveLock] = useState(null);

  // Modal açıldığında kilit kontrolü yap
  useEffect(() => {
    const checkLockAndFetchCustomers = async () => {
      const db = getFirestore();
      const userEmail = auth.currentUser?.email;
      
      if (!userEmail) {
        setError('Kullanıcı oturumu bulunamadı');
        setAlertOpen(true);
        return;
      }

      try {
        // Önce kilit kontrolü yap
        const lockStatus = await checkTransferLock(userEmail);
        if (lockStatus.isLocked) {
          setActiveLock(lockStatus);
        } else {
          setActiveLock(null);
        }

        // Müşterileri getir
        const customerListRef = collection(db, `users/${userEmail}/customerList`);
        const querySnapshot = await getDocs(customerListRef);
        const customers = querySnapshot.docs
          .map(doc => ({
            id: doc.id,
            ...doc.data()
          }))
          .filter(customer => customer.id !== selectedCustomer?.id);
        
        setAvailableCustomers(customers);
      } catch (error) {
        console.error('Müşteriler getirilirken hata:', error);
        setError('Müşteriler getirilirken bir hata oluştu');
        setAlertOpen(true);
      }
    };

    if (isOpen) {
      checkLockAndFetchCustomers();
    }
  }, [isOpen, selectedCustomer?.id, setAlertOpen, setError]);

  const handleTransferClick = async () => {
    // Transfer başlamadan önce son bir kilit kontrolü daha yap
    const userEmail = auth.currentUser?.email;
    if (userEmail) {
      const lockStatus = await checkTransferLock(userEmail);
      if (lockStatus.isLocked) {
        setActiveLock(lockStatus);
        setError(`Başka bir transfer işlemi devam ediyor: ${lockStatus.sourceCustomerName} → ${lockStatus.targetCustomerName}. Lütfen ${lockStatus.remainingSeconds} saniye bekleyin.`);
        setAlertOpen(true);
        return;
      }
    }
    setIsConfirmTransferModalOpen(true);
  };

  const handleConfirmTransfer = async () => {
    setIsConfirmTransferModalOpen(false);
    await handleDataTransfer({
      sourceCustomer: selectedCustomer,
      targetCustomer: selectedTargetCustomer,
      isSantiyeTransfer,
      setError,
      setSuccessMessage,
      setAlertOpen,
      onSuccess,
      setIsProcessing // Loading state'i kontrol etmek için
    });
    if (!isProcessing) {
      onClose(); // Popup'ı kapat (sadece işlem bittiğinde)
    }
  };

  const handleCancelTransfer = () => {
    setIsConfirmTransferModalOpen(false);
  };

  // Modal kapatılmaya çalışıldığında işlem devam ediyorsa engelle
  const handleClose = () => {
    if (isProcessing) {
      setError('Transfer işlemi devam ediyor, lütfen bekleyin...');
      setAlertOpen(true);
      return;
    }
    onClose();
  };

  // Kaynak müşteri adı ve cari kodu için öncelik: editName/editCariCode > selectedCustomer
  const sourceName = editName || (isSantiyeTransfer 
    ? (selectedCustomer && (selectedCustomer['Şantiye Adı'] || selectedCustomer['Müşteri Adı'])) 
    : (selectedCustomer && selectedCustomer['Müşteri Adı']));
  const sourceCari = editCariCode || (isSantiyeTransfer 
    ? (selectedCustomer && (selectedCustomer['Şantiye Cari Kodu'] || selectedCustomer.cariCode))
    : (selectedCustomer && selectedCustomer.cariCode));

  return (
    <Modal open={isOpen} onClose={handleClose}>
      <ModalBox>
        {/* Loading Overlay */}
        <Backdrop
          sx={{ 
            position: 'absolute',
            zIndex: (theme) => theme.zIndex.drawer + 1,
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            display: 'flex',
            flexDirection: 'column',
            gap: 2
          }}
          open={isProcessing}
        >
          <CircularProgress color="primary" size={60} />
          <Typography variant="h6" color="primary">
            Transfer işlemi devam ediyor...
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Lütfen sayfayı kapatmayın veya yenilemeyin.
          </Typography>
        </Backdrop>

        <Typography variant="h6" gutterBottom>
          {isSantiyeTransfer ? 'Şantiye Verilerini Aktar' : 'Müşteri Verilerini Aktar'}
        </Typography>

        {/* Aktif kilit uyarısı */}
        {activeLock && (
          <Box 
            sx={{ 
              mb: 2, 
              p: 2, 
              backgroundColor: '#fff3e0', 
              border: '1px solid #ff9800',
              borderRadius: 1
            }}
          >
            <Typography variant="body2" color="warning.dark" fontWeight={600}>
              ⚠️ Başka bir transfer işlemi devam ediyor:
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {activeLock.sourceCustomerName} → {activeLock.targetCustomerName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Kalan süre: ~{activeLock.remainingSeconds} saniye
            </Typography>
          </Box>
        )}

        {selectedCustomer && (
          <Box mb={2}>
            <Typography>
              <strong>Müşteri Adı:</strong> {sourceName}
            </Typography>
            <Typography>
              <strong>Cari Kodu:</strong> {sourceCari}
            </Typography>
          </Box>
        )}

        <Box mt={2} mb={2}>
          <Typography variant="subtitle1" gutterBottom>
            Verilerin aktarılacağı {isSantiyeTransfer ? 'şantiyeyi' : 'müşteriyi'} seçin:
          </Typography>
          <Box sx={{ 
            maxHeight: '400px', 
            overflow: 'auto',
            border: '1px solid #e0e0e0',
            borderRadius: '4px',
            mb: 2,
            opacity: activeLock ? 0.5 : 1,
            pointerEvents: activeLock ? 'none' : 'auto'
          }}>
            <CustomerSelectionTable
              customers={availableCustomers}
              onSelectCustomer={setSelectedTargetCustomer}
              selectedId={selectedTargetCustomer?.id}
              enableAutoUppercase={true}
            />
          </Box>
        </Box>

        <Typography variant="body2" color="text.secondary" gutterBottom>
          <strong>Uyarı:</strong> Seçilen {isSantiyeTransfer ? 'şantiyenin' : 'müşterinin'} puantaj verileri 
          hedef {isSantiyeTransfer ? 'şantiyeye' : 'müşteriye'} aktarılacak ve kaynak {isSantiyeTransfer ? 'şantiye' : 'müşteri'} silinecektir.
        </Typography>
        <Typography variant="body2" color="error" gutterBottom>
          Bu işlem geri alınamaz!
        </Typography>

        <Box mt={2} display="flex" justifyContent="flex-end" gap={2}>
          <Button variant="outlined" onClick={handleClose} disabled={isProcessing}>
            İptal
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleTransferClick}
            disabled={!selectedTargetCustomer || isProcessing || activeLock}
          >
            {isProcessing ? 'İşleniyor...' : 'Aktar'}
          </Button>
        </Box>

        {/* Aktarım onay popupı */}
        <Modal open={isConfirmTransferModalOpen} onClose={handleCancelTransfer}>
          <ModalBox>
            <Box display="flex" alignItems="center" mb={2}>
              <span style={{ fontSize: 32, color: '#e53935', marginRight: 12 }}>⚠️</span>
              <Typography variant="h6" fontWeight={700} color="#e53935">
                Aktarım Onayı
              </Typography>
            </Box>
            <Typography variant="body1" gutterBottom>
              Seçili müşterinin tüm puantaj ve cari kasa verileri aşağıda seçtiğiniz müşteriye aktarılacak.<br/>
              <b>Aktarım sonrası kaynak müşteri sistemden silinecek.</b> Bu işlem <b>geri alınamaz</b>.<br/><br/>
              Lütfen aktarım bilgilerini dikkatlice kontrol edin ve emin değilseniz vazgeçin.
            </Typography>
            <Box mt={2} mb={2}>
              <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fafafa', borderRadius: 8 }}>
                <thead>
                  <tr style={{ background: '#f5f5f5' }}>
                    <th style={{ textAlign: 'left', padding: 8, fontWeight: 600, color: '#888' }}>Alan</th>
                    <th style={{ textAlign: 'left', padding: 8, fontWeight: 600, color: '#888' }}>Kaynak Müşteri</th>
                    <th style={{ textAlign: 'left', padding: 8, fontWeight: 600, color: '#888' }}>Hedef Müşteri</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={{ padding: 8 }}>Müşteri Adı</td>
                    <td style={{ padding: 8, color: '#e53935' }}>{sourceName}</td>
                    <td style={{ padding: 8, color: '#43a047' }}>{selectedTargetCustomer?.['Müşteri Adı']}</td>
                  </tr>
                  <tr>
                    <td style={{ padding: 8 }}>Cari Kod</td>
                    <td style={{ padding: 8, color: '#e53935' }}>{sourceCari}</td>
                    <td style={{ padding: 8, color: '#43a047' }}>{selectedTargetCustomer?.cariCode}</td>
                  </tr>
                </tbody>
              </table>
            </Box>
            <Typography variant="body2" color="#e53935" fontWeight={500} mb={2}>
              Bu işlem geri alınamaz. Kaynak müşteri ve tüm bağlı kayıtlar silinecek.
            </Typography>
            <Box mt={2} display="flex" justifyContent="flex-end" gap={2}>
              <Button variant="outlined" color="inherit" onClick={handleCancelTransfer}>
                Vazgeç
              </Button>
              <Button variant="contained" color="error" onClick={handleConfirmTransfer}>
                Evet, aktarımı onaylıyorum
              </Button>
            </Box>
          </ModalBox>
        </Modal>
      </ModalBox>
    </Modal>
  );
};

// Export helper functions for use in other components
export { 
  deleteCustomerWithDebt, 
  deleteDebtSubcollection,
  // Kilit yönetimi fonksiyonları
  acquireTransferLock,
  releaseTransferLock,
  checkTransferLock
};

export default TransferCustomerModal;
